import { computed, ref, watch } from 'vue'
import type { LocationQueryRaw } from 'vue-router'
import { useRoute, useRouter } from 'vue-router'
import {
  getDialerQueue,
  getDialerQueueFilterOptions,
  saveDialerEntryNotes,
  type CallerQueueEntry,
  type QueueSortMode,
} from '../../services/dialerApi'
import { railSection } from '../components/dialer/dialability'
import { useCurrentUser } from '../../services/user'
import { linkedContactIds } from '../utils/dialLink'

export interface SalesPlayGroup {
  /** null: ad-hoc call list entries have no play. */
  salesPlayId: string | null
  salesPlayName: string | null
  color: string // 'var(--hue-…)'
  blurb: string | null // sales_play_description
  entries: CallerQueueEntry[]
}

export type RailSectionKey = 'warm' | 'cold' | 'cooldown' | 'noNumber'

export interface RailSection {
  key: RailSectionKey
  /** null → warm (the rail's own head covers it), and cold when there's no warm to contrast with. */
  title: string | null
  /** Ungrouped sorts emit one synthetic group whose header doesn't render. */
  groups: SalesPlayGroup[]
  grouped: boolean
  total: number
  open: boolean
  collapsible: boolean
}

export const QUEUE_SORT_OPTIONS: { value: QueueSortMode; label: string }[] = [
  { value: 'sales_play', label: 'Sales play' },
  { value: 'least_attempts', label: 'Least attempts' },
  { value: 'longest_since', label: 'Longest since last attempt' },
  { value: 'name_asc', label: 'Name (A–Z)' },
  { value: 'name_desc', label: 'Name (Z–A)' },
]

// A rep's default dial list is named with their own user id: a key, not a label.
const MY_CALL_LIST = 'My Call List'

const DEFAULT_SORT: QueueSortMode = 'sales_play'
const VALID_SORTS = new Set<string>(QUEUE_SORT_OPTIONS.map((o) => o.value))

// Never-attempted → epoch 0, not -Infinity: two -Infinity values subtract to
// NaN, which is undefined behavior in a comparator. Real timestamps are always
// > 0, so never-attempted still sorts first.
function lastAttemptTs(e: CallerQueueEntry): number {
  return e.last_human_attempt_at ? Date.parse(e.last_human_attempt_at) : 0
}

// Unnamed contacts sort last in BOTH directions — a name sort is for finding
// people by name, and among themselves they keep the server order.
function byName(dir: 1 | -1) {
  return (a: CallerQueueEntry, b: CallerQueueEntry) => {
    if (!a.contact_name && !b.contact_name) return 0
    if (!a.contact_name) return 1
    if (!b.contact_name) return -1
    return dir * a.contact_name.localeCompare(b.contact_name, undefined, { sensitivity: 'base' })
  }
}

// Display order within each section. The server picks WHICH people make the
// capped window (same sort key); these order them inside the rail's sections.
// Array.prototype.sort is stable, so remaining ties keep the server's order.
const SORT_COMPARATORS: Record<
  Exclude<QueueSortMode, 'sales_play'>,
  (a: CallerQueueEntry, b: CallerQueueEntry) => number
> = {
  least_attempts: (a, b) =>
    a.human_attempts - b.human_attempts || lastAttemptTs(a) - lastAttemptTs(b),
  longest_since: (a, b) => lastAttemptTs(a) - lastAttemptTs(b),
  name_asc: byName(1),
  name_desc: byName(-1),
}

// Ordered hue palette — assigned by a sales play's first appearance in the queue
// so each group reads as a distinct colour. Stable across warm + cold.
const HUE_PALETTE = [
  'var(--hue-blue)',
  'var(--hue-amber)',
  'var(--hue-green)',
  'var(--hue-purple)',
  'var(--hue-gray)',
]

export function useDialerQueue() {
  const entries = ref<CallerQueueEntry[]>([])
  const loading = ref(false)
  const loaded = ref(false)
  // Whether the rows on screen came from a filtered read — not the URL, which moves first.
  const rowsFiltered = ref(false)
  const error = ref<string | null>(null)
  const selectedId = ref<string | null>(null)
  // Whether cold contacts are included in the dialable set (the rail's eye
  // toggle).
  const showCold = ref(true)
  // The two non-dialable sections (Called recently / Outside supported regions) are
  // collapsed by default like cold; load() opens whichever holds the lead.
  const showCooldown = ref(false)
  const showNoNumber = ref(false)

  const route = useRoute()
  const router = useRouter()
  const { isDialerOnly, me } = useCurrentUser()
  const listLabel = (name: string): string => (name === me.value?.user_id ? MY_CALL_LIST : name)

  // Sort + filters live in the URL (?sort= ?play= ?account=) — the same
  // URL-as-state contract as useCandidateQuery/useActionsQuery, so the rail is
  // deep-linkable and back/forward works.
  const queryStr = (key: string): string => {
    const raw = route.query[key]
    const value = Array.isArray(raw) ? raw[0] : raw
    return typeof value === 'string' ? value.trim() : ''
  }

  // Multi-field writes MUST go through one pushQuery call (like clearFilters):
  // two sequential setter writes would each read the not-yet-navigated route
  // and the second would drop the first.
  function pushQuery(patch: { sort?: QueueSortMode; play?: string; account?: string }): void {
    const next = {
      sort: patch.sort ?? sortMode.value,
      play: patch.play ?? filterPlayId.value,
      account: patch.account ?? filterAccountId.value,
    }
    // Value-level no-op guard (key-order-proof): SearchSelect re-emits on
    // re-picking the same option, and a junk history entry costs a back press.
    if (next.sort === sortMode.value && next.play === filterPlayId.value && next.account === filterAccountId.value) {
      return
    }
    const query: LocationQueryRaw = { ...route.query }
    delete query.sort
    delete query.play
    delete query.account
    if (next.sort !== DEFAULT_SORT) query.sort = next.sort
    if (next.play) query.play = next.play
    if (next.account) query.account = next.account
    void router.push({ query })
  }

  const sortMode = computed<QueueSortMode>({
    // Unrecognized values fall back to the default rather than being passed
    // through — the server would reject them, and a silently unsorted rail is
    // worse than an honest default.
    get: () => (VALID_SORTS.has(queryStr('sort')) ? (queryStr('sort') as QueueSortMode) : DEFAULT_SORT),
    set: (v) => pushQuery({ sort: v }),
  })
  const linkedIds = computed(() => new Set(linkedContactIds(route.query.selected)))

  const filterPlayId = computed<string>({
    // Inert for dialer-only while the list filter is hidden (one list per rep), so a
    // leftover ?play= can't narrow the queue or show as an active filter.
    get: () => (isDialerOnly.value ? '' : queryStr('play')),
    set: (v) => pushQuery({ play: v }),
  })
  const filterAccountId = computed<string>({
    get: () => queryStr('account'),
    set: (v) => pushQuery({ account: v }),
  })
  const activeFilterCount = computed(
    () => (filterPlayId.value ? 1 : 0) + (filterAccountId.value ? 1 : 0),
  )

  // id → hue, in queue order (warm-first, then cold). Memoised per entries change.
  const playColorMap = computed(() => {
    const map = new Map<string, string>()
    let i = 0
    for (const e of entries.value) {
      if (e.sales_play_id && !map.has(e.sales_play_id)) {
        map.set(e.sales_play_id, HUE_PALETTE[i % HUE_PALETTE.length])
        i++
      }
    }
    return map
  })
  function playColor(id: string | null | undefined): string {
    return (id && playColorMap.value.get(id)) || 'var(--hue-gray)'
  }

  // Only 'ready' contacts are dialable (warm/cold). Cooldown + no-local-number
  // entries carry a warm/cold label too, but are excluded here so they never enter
  // `visibleEntries` (the dialing-session snapshot) — the load-bearing safety edit.
  const inSection = (name: ReturnType<typeof railSection>) =>
    computed(() => {
      const list = entries.value.filter((e) => railSection(e.dialability, e.label) === name)
      if (sortMode.value !== 'sales_play') list.sort(SORT_COMPARATORS[sortMode.value])
      else list.sort((a, b) => Number(b.sales_play_name === MY_CALL_LIST) - Number(a.sales_play_name === MY_CALL_LIST))
      return list
    })
  const warm = inSection('warm')
  const cold = inSection('cold')
  // With no warm contacts cold loses its header (and eye toggle).
  const coldOpen = computed(() => showCold.value || !warm.value.length)
  // Non-dialable buckets, shown in their own rail sections below cold.
  const cooldown = inSection('cooldown')
  const noNumber = inSection('noNumber')

  // Contiguous runs by sales play — the backend returns each section's plays
  // alphabetically.
  function groupBy(list: CallerQueueEntry[]): SalesPlayGroup[] {
    const groups: SalesPlayGroup[] = []
    for (const e of list) {
      const last = groups[groups.length - 1]
      if (last && last.salesPlayId === e.sales_play_id) {
        last.entries.push(e)
      } else {
        groups.push({
          salesPlayId: e.sales_play_id,
          salesPlayName: e.sales_play_name,
          color: playColor(e.sales_play_id),
          blurb: e.sales_play_description,
          entries: [e],
        })
      }
    }
    return groups
  }

  function sectionGroups(
    key: RailSectionKey,
    list: CallerQueueEntry[],
    grouped: boolean,
  ): SalesPlayGroup[] {
    if (grouped) return groupBy(list)
    if (!list.length) return []
    return [{ salesPlayId: key, salesPlayName: null, color: '', blurb: null, entries: list }]
  }

  const sections = computed<RailSection[]>(() => {
    const grouped = sortMode.value === 'sales_play'
    const defs: {
      key: RailSectionKey
      list: CallerQueueEntry[]
      open: boolean
      collapsible: boolean
      title: string | null
    }[] = [
      { key: 'warm', list: warm.value, open: true, collapsible: false, title: null },
      {
        key: 'cold',
        list: cold.value,
        open: coldOpen.value,
        collapsible: true,
        title: !warm.value.length
          ? null
          : grouped && !isDialerOnly.value
            ? 'Cold contacts by sales play'
            : 'Cold contacts',
      },
      {
        key: 'cooldown',
        list: cooldown.value,
        open: showCooldown.value,
        collapsible: true,
        title: 'Called recently',
      },
      {
        key: 'noNumber',
        list: noNumber.value,
        open: showNoNumber.value,
        collapsible: true,
        title: 'Outside supported regions',
      },
    ]
    return defs.map((d) => ({
      key: d.key,
      title: d.title,
      grouped,
      groups: sectionGroups(d.key, d.list, grouped),
      total: d.list.length,
      open: d.open,
      collapsible: d.collapsible,
    }))
  })

  function toggleSection(key: RailSectionKey) {
    if (key === 'cold') showCold.value = !showCold.value
    else if (key === 'cooldown') showCooldown.value = !showCooldown.value
    else if (key === 'noNumber') showNoNumber.value = !showNoNumber.value
  }

  // The dialable set: warm always, cold only when toggled on. Section order
  // (warm then cold) matching the rail's top-down walk. Single source for the
  // "X can be power dialed" count and the dialing session's frozen queue snapshot.
  const visibleEntries = computed(() =>
    coldOpen.value ? [...warm.value, ...cold.value] : [...warm.value],
  )
  // The rail's full top-down row order, across all four sections.
  const railOrder = computed(() => [
    ...warm.value,
    ...cold.value,
    ...cooldown.value,
    ...noNumber.value,
  ])
  const selected = computed(
    () => entries.value.find((e) => e.id === selectedId.value) || null,
  )

  // The focus panel falls through to the first available contact (rail order:
  // warm → cold → cooldown → no-number). Reveal whichever section holds the
  // selected lead so the rail never hides what the panel shows.
  function revealSelectedSection() {
    const lead = entries.value.find((e) => e.id === selectedId.value) || null
    const section = lead ? railSection(lead.dialability, lead.label) : null
    if (section === 'cooldown') showCooldown.value = true
    else if (section === 'noNumber') showNoNumber.value = true
    else if (section === 'cold') showCold.value = true
  }

  // Monotonic load counter: watcher-driven refetches can overlap, and a slower
  // stale response must never overwrite a newer one (the rail would contradict
  // the active filters and the dialing run's frozen snapshot).
  let loadSeq = 0

  async function load(opts?: { resetSelection?: boolean }) {
    const seq = ++loadSeq
    loading.value = true
    error.value = null
    try {
      const filtered = activeFilterCount.value > 0
      // The server scopes a dialer-only rep to their own dial lists.
      const rows = await getDialerQueue({
        salesPlay: filterPlayId.value || undefined,
        accountId: filterAccountId.value || undefined,
        sort: sortMode.value,
      })
      if (seq !== loadSeq) return
      // The SDR's place is the person, not their row: a row's id is its best
      // line, which moves as numbers rest.
      const placeContact = opts?.resetSelection ? null : (selected.value?.contact_id ?? null)
      entries.value = rows.map((e) => ({
        ...e,
        sales_play_name: e.sales_play_name && listLabel(e.sales_play_name),
      }))
      rowsFiltered.value = filtered
      // Keep the current selection across a refresh only when the person is still
      // there AND the caller didn't ask to reset. After a dialing session ends we
      // reset to the top of the refreshed queue (or the ?selected person, when the
      // URL names one): a just-dialed contact is now tagged 'cooldown' (so it
      // survives in the list) and would otherwise stay selected instead of the top
      // ready contact the SDR expects to land on. A filter or sort change rides the
      // same rule: a selection that drops out of the new window falls to the top of
      // the new rail order.
      const kept = placeContact ? railOrder.value.find((e) => e.contact_id === placeContact) : undefined
      if (kept) {
        selectedId.value = kept.id
      } else {
        // Anyone we cannot find (exited, filtered out, or past the server's row cap)
        // lands on the top of the rail exactly as before.
        const deepLinked = railOrder.value.find((e) => linkedIds.value.has(e.contact_id))
        selectedId.value = (deepLinked ?? railOrder.value[0])?.id ?? null
      }
      revealSelectedSection()
      // Keep the play dropdown in step with the rows: bulk exits and freshly
      // entered plays change the option set, not just the entries.
      void loadFilterOptions()
    } catch (e: any) {
      if (seq !== loadSeq) return
      error.value = e?.message || 'Failed to load the caller queue.'
      entries.value = []
      selectedId.value = null
    } finally {
      if (seq === loadSeq) {
        loading.value = false
        loaded.value = true
      }
    }
  }

  // One watcher owns the refetch. Filters compose independently — a play change
  // narrows AROUND an applied account filter.
  // Route-backed sources also "change" while navigating AWAY (the reactive
  // route flips to the destination before unmount) — don't fire a farewell
  // fetch of the unfiltered queue.
  const mountedPath = route.path
  watch([sortMode, filterPlayId, filterAccountId], () => {
    if (route.path !== mountedPath) return
    void load()
  })

  function clearFilters() {
    // One push, one history entry — not two sequential setter writes.
    pushQuery({ play: '', account: '' })
  }

  // ── Filter dropdown sources ──────────────────────────────────────
  // From the live queue population (not the capped response), so no option can
  // point at an empty rail. Plays are few and static per load; accounts can run
  // to hundreds, so they're searched server-side per keystroke.

  const playOptions = ref<{ value: string; label: string }[]>([])

  async function loadFilterOptions() {
    try {
      const opts = await getDialerQueueFilterOptions()
      playOptions.value = opts.salesPlays.map((p) => ({ value: p.id, label: listLabel(p.name) }))
    } catch {
      /* leave the previous options in place; the rows still load */
    }
  }

  async function searchAccounts(query: string) {
    try {
      const opts = await getDialerQueueFilterOptions({
        salesPlay: filterPlayId.value || undefined,
        q: query || undefined,
      })
      return opts.accounts.map((a) => ({ value: a.id, label: a.name }))
    } catch {
      return []
    }
  }

  function select(id: string) {
    selectedId.value = id
  }

  // Persist notes (optimistic local update + fire-and-forget PUT) — on blur.
  async function saveNotes(entryId: string, notes: string | null) {
    const val = String(notes || '').trim() || null
    const e = entries.value.find((x) => x.id === entryId)
    if (e) e.notes = val
    try {
      await saveDialerEntryNotes(entryId, val)
    } catch {
      /* best-effort */
    }
  }

  return {
    entries,
    loading,
    loaded,
    error,
    selectedId,
    selected,
    warm,
    cold,
    cooldown,
    noNumber,
    sections,
    sortMode,
    filterPlayId,
    filterAccountId,
    activeFilterCount,
    rowsFiltered,
    playOptions,
    showCold,
    showCooldown,
    showNoNumber,
    visibleEntries,
    railOrder,
    playColor,
    select,
    load,
    loadFilterOptions,
    searchAccounts,
    clearFilters,
    toggleSection,
    saveNotes,
  }
}
